/**
 * Icon registry -- imports the app icon set from the Sharp DuoTone Solid pack
 * and registers it with the FontAwesome SVG core library.
 *
 * This file must be imported once at app startup (before any Icon component
 * renders) so that `findIconDefinition` can resolve icons by prefix + name.
 *
 * The app ships a single icon pack. `FALLBACK_PREFIX` (Sharp DuoTone Solid)
 * matches this pack, so the Icon component always resolves a glyph.
 */

import { library } from '@fortawesome/fontawesome-svg-core';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// ---------------------------------------------------------------------------
// Sharp DuoTone Solid
// ---------------------------------------------------------------------------

import {
  faHouse as fasdsHouse, faMessage as fasdsMessage, faMessages as fasdsMessages,
  faReply as fasdsReply, faUsers as fasdsUsers,
  faGear as fasdsGear, faCircleInfo as fasdsCircleInfo, faDownload as fasdsDownload,
  faRightFromBracket as fasdsLogout, faShieldCheck as fasdsShield,
  faKey as fasdsKey, faBell as fasdsBell, faMagnifyingGlass as fasdsSearch,
  faUser as fasdsUser, faPalette as fasdsPalette, faLock as fasdsLock,
  faMask as fasdsMask, faPlus as fasdsPlus, faCheck as fasdsCheck,
  faClock as fasdsClock, faTimer as fasdsTimer, faXmark as fasdsX, faGrid2 as fasdsSpaces,
  faEllipsis as fasdsEllipsis, faEllipsisVertical as fasdsEllipsisVertical,
  faLocationPin as fasdsLocationPin, faThumbtack as fasdsThumbtack, faFaceSmile as fasdsSmile,
  faFaceSmilePlus as fasdsSmilePlus, faPaperPlane as fasdsSend,
  faMailbox as fasdsMailbox,
  faMessageArrowUp as fasdsMessageArrowUp,
  faMessageArrowUpRight as fasdsMessageArrowUpRight,
  faTrash as fasdsTrash, faFileExport as fasdsFileExport,
  faFileImport as fasdsFileImport, faChevronDown as fasdsChevronDown,
  faChevronRight as fasdsChevronRight, faChevronUp as fasdsChevronUp,
  faCamera as fasdsCamera, faImage as fasdsImage, faGlobe as fasdsGlobe,
  faEarth as fasdsEarth,
  faDesktop as fasdsDesktop, faServer as fasdsServer, faBars as fasdsBars, faEye as fasdsEye,
  faThumbsUp as fasdsThumbsUp, faTriangleExclamation as fasdsWarning,
  faCircleCheck as fasdsSuccess, faCircleXmark as fasdsError,
  faBadgeCheck as fasdsBadge, faFileArrowDown as fasdsFileDown,
  faFileArrowUp as fasdsFileUp, faArrowLeft as fasdsArrowLeft, faArrowRight as fasdsArrowRight,
  faArrowLeftArrowRight as fasdsArrowLeftArrowRight,
  faCircle as fasdsCircle, faPen as fasdsPen,
  faNoteSticky as fasdsNoteSticky,
  faTrophy as fasdsTrophy, faUserPlus as fasdsUserPlus,
  faUserGroup as fasdsUserGroup, faMobileScreenButton as fasdsMobile,
  faHeart as fasdsHeart, faBan as fasdsBan, faGavel as fasdsGavel,
  faFire as fasdsFire, faUserMinus as fasdsUserMinus,
  faBoxArchive as fasdsBoxArchive, faStar as fasdsStar,
  faFilter as fasdsFilter, faArrowDownAZ as fasdsArrowDownAZ,
  faArrowUpArrowDown as fasdsArrowUpArrowDown,
  faCopy as fasdsCopy, faFiles as fasdsFiles, faBorderAll as fasdsBorderAll, faLink as fasdsLink,
  faAt as fasdsAt,
  faArrowUpRightFromSquare as fasdsExternalLink,
  faArrowUpFromBracket as fasdsArrowUpFromBracket, faFilm as fasdsFilm,
  faPhone as fasdsPhone, faPhoneHangup as fasdsPhoneHangup, faVideo as fasdsVideo,
  faVideoSlash as fasdsVideoSlash, faMicrophone as fasdsMicrophone,
  faMicrophoneSlash as fasdsMicrophoneSlash,
  faVolumeHigh as fasdsVolumeHigh, faVolumeSlash as fasdsVolumeSlash,
  faWaveform as fasdsWaveform,
  faScreenUsers as fasdsScreenUsers,
  faMaximize as fasdsMaximize, faCompress as fasdsCompress,
  faArrowsToEye as fasdsArrowsToEye,
  faPhoneArrowDownLeft as fasdsPhoneArrowDownLeft,
  faEyeSlash as fasdsEyeSlash,
  faSignalBars as fasdsSignalBars, faSignalBarsGood as fasdsSignalBarsGood,
  faSignalBarsFair as fasdsSignalBarsFair, faSignalBarsWeak as fasdsSignalBarsWeak,
  faHandshake as fasdsHandshake,
  faPersonPregnant as fasdsPersonPregnant,
  faCreditCard as fasdsCreditCard,
  faCoconut as fasdsCoconut,
  faKidneys as fasdsKidneys,
  faRabbit as fasdsRabbit,
  faRouter as fasdsRouter,
  faCrown as fasdsCrown,
  faHandFist as fasdsHandFist,
  faUserSecret as fasdsUserSecret,
  faHatWizard as fasdsHatWizard,
  faMoon as fasdsMoon,
  faHourglassHalf as fasdsHourglassHalf,
  faExplosion as fasdsExplosion,
  faDoorOpen as fasdsDoorOpen,
  faIslandTropical as fasdsIslandTropical,
  faGift as fasdsGift,
  faSuitcase as fasdsSuitcase,
  faLandmark as fasdsLandmark,
  faMugSaucer as fasdsMugSaucer,
  faParachuteBox as fasdsParachuteBox,
  faBriefcase as fasdsBriefcase,
  faPizzaSlice as fasdsPizzaSlice,
  faAward as fasdsAward,
  faHandcuffs as fasdsHandcuffs,
  faHatChef as fasdsHatChef,
  faTemperatureFull as fasdsTemperatureFull,
  faSnowflake as fasdsSnowflake,
  faDragon as fasdsDragon,
  faSkull as fasdsSkull,
  faWineGlass as fasdsWineGlass,
  faUtensils as fasdsUtensils,
  faLamp as fasdsLamp,
  faBoxBallot as fasdsBoxBallot,
  faBallotCheck as fasdsBallotCheck,
  faTooth as fasdsTooth,
  faBottleWater as fasdsBottleWater,
  faCrab as fasdsCrab,
  faLeaf as fasdsLeaf,
  faFolder as fasdsFolder,
  faFolders as fasdsFolders,
  faLayerGroup as fasdsLayerGroup,
  faBallPile as fasdsBallPile,
  faBuilding as fasdsBuilding,
  faFamily as fasdsFamily,
  faSportsball as fasdsSportsball,
  faDice as fasdsDice,
  faDiceD10 as fasdsDiceD10,
  faDiceD12 as fasdsDiceD12,
  faGameBoard as fasdsGameBoard,
  faGameConsoleHandheld as fasdsGameConsoleHandheld,
  faArrowRotateRight as fasdsArrowRotateRight,
  faArrowRotateLeft as fasdsArrowRotateLeft,
  faCakeCandles as fasdsCakeCandles,
  faEnvelope as fasdsEnvelope,
} from '@adieuu-llc/fa-sharp-duotone-solid-svg-icons';

// ---------------------------------------------------------------------------
// Register everything with the FA library
// ---------------------------------------------------------------------------

const allIcons: IconDefinition[] = [
  fasdsHouse, fasdsMessage, fasdsMessages, fasdsReply, fasdsUsers, fasdsGear, fasdsCircleInfo, fasdsDownload,
  fasdsLogout, fasdsShield, fasdsKey, fasdsBell, fasdsSearch, fasdsUser, fasdsPalette,
  fasdsLock, fasdsMask, fasdsPlus, fasdsCheck, fasdsClock, fasdsTimer, fasdsX, fasdsSpaces,
  fasdsEllipsis, fasdsEllipsisVertical, fasdsLocationPin, fasdsThumbtack, fasdsSmile, fasdsSmilePlus, fasdsSend, fasdsMailbox, fasdsMessageArrowUp, fasdsMessageArrowUpRight, fasdsTrash, fasdsFileExport,
  fasdsFileImport, fasdsChevronDown, fasdsChevronRight, fasdsChevronUp, fasdsCamera,
  fasdsImage, fasdsGlobe, fasdsEarth, fasdsDesktop, fasdsServer, fasdsBars, fasdsEye, fasdsThumbsUp,
  fasdsWarning, fasdsSuccess, fasdsError, fasdsBadge, fasdsFileDown, fasdsFileUp,
  fasdsArrowLeft, fasdsArrowRight, fasdsArrowLeftArrowRight, fasdsCircle, fasdsPen, fasdsNoteSticky,
  fasdsTrophy, fasdsUserPlus, fasdsUserGroup, fasdsMobile, fasdsHeart, fasdsBan, fasdsGavel,
  fasdsFire, fasdsUserMinus,
  fasdsBoxArchive, fasdsStar, fasdsFilter, fasdsArrowDownAZ, fasdsArrowUpArrowDown,
  fasdsCopy, fasdsFiles, fasdsBorderAll, fasdsLink, fasdsAt, fasdsExternalLink, fasdsArrowUpFromBracket, fasdsFilm,
  fasdsPhone, fasdsPhoneHangup, fasdsVideo, fasdsVideoSlash, fasdsMicrophone,
  fasdsMicrophoneSlash, fasdsVolumeHigh, fasdsVolumeSlash, fasdsWaveform, fasdsScreenUsers,
  fasdsPhoneArrowDownLeft, fasdsEyeSlash,
  fasdsMaximize, fasdsCompress, fasdsArrowsToEye,
  fasdsSignalBars, fasdsSignalBarsGood, fasdsSignalBarsFair, fasdsSignalBarsWeak,
  fasdsHandshake,
  fasdsPersonPregnant,
  fasdsCreditCard,
  fasdsCoconut,
  fasdsKidneys,
  fasdsRabbit,
  fasdsRouter,
  fasdsCrown,
  fasdsHandFist,
  fasdsUserSecret,
  fasdsHatWizard,
  fasdsMoon,
  fasdsHourglassHalf,
  fasdsExplosion,
  fasdsDoorOpen,
  fasdsIslandTropical,
  fasdsGift,
  fasdsSuitcase,
  fasdsLandmark,
  fasdsMugSaucer,
  fasdsParachuteBox,
  fasdsBriefcase,
  fasdsPizzaSlice,
  fasdsAward,
  fasdsHandcuffs,
  fasdsHatChef,
  fasdsTemperatureFull,
  fasdsSnowflake,
  fasdsDragon,
  fasdsSkull,
  fasdsWineGlass,
  fasdsUtensils,
  fasdsLamp,
  fasdsBoxBallot,
  fasdsBallotCheck,
  fasdsTooth,
  fasdsBottleWater,
  fasdsCrab,
  fasdsLeaf,
  fasdsFolder,
  fasdsFolders,
  fasdsLayerGroup,
  fasdsBallPile,
  fasdsBuilding,
  fasdsFamily,
  fasdsSportsball,
  fasdsDice,
  fasdsDiceD10,
  fasdsDiceD12,
  fasdsGameBoard,
  fasdsGameConsoleHandheld,
  fasdsArrowRotateRight,
  fasdsArrowRotateLeft,
  fasdsCakeCandles,
  fasdsEnvelope,
];

library.add(...allIcons);
